import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Customer, Quote } from "@/types/crm";
import { useQuoteForm } from "../../../hooks/useQuoteForm";
import BasicQuoteFields from "./shared/BasicQuoteFields";
import LineItemsSection from "./shared/LineItemsSection";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  seller_email?: string;
  seller_phone?: string;
  buyer_name: string;
  current_balance?: number;
  payment_amount?: number;
  payments_remaining?: number;
  property_address?: string;
}

interface CustomerQuoteFormProps {
  customers: Customer[];
  onQuoteCreated: () => void;
  onCancel: () => void;
  editingQuote?: Quote;
  selectedDeal?: Deal;
}

const DEFAULT_CUSTOMER_LINE_ITEMS = [
  { description: "Cash Purchase Price for Land Contract", quantity: 1, unit_price: 0, line_total: 0 },
  { description: "Less: Deed Transfer Tax (Customer Responsibility)", quantity: 1, unit_price: 0, line_total: 0 },
];

const CustomerQuoteForm = ({ customers, onQuoteCreated, onCancel, editingQuote, selectedDeal }: CustomerQuoteFormProps) => {
  const { toast } = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState(selectedDeal?.id || "");
  const [notes, setNotes] = useState(editingQuote?.notes || "");
  const [firstPaymentDate, setFirstPaymentDate] = useState("");
  const [numberOfPayments, setNumberOfPayments] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  
  const {
    formData,
    lineItems,
    isLoading,
    updateFormData,
    updateLineItem,
    addLineItem,
    removeLineItem,
    calculateTotals,
    validateForm,
    submitQuote,
  } = useQuoteForm({
    defaultLineItems: DEFAULT_CUSTOMER_LINE_ITEMS,
    onSuccess: onQuoteCreated,
    editingQuote,
  });

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    if (selectedDeal && customers.length > 0) {
      console.log('selectedDeal changed, populating form...', selectedDeal);
      populateFromDeal(selectedDeal);
    }
  }, [selectedDeal, customers]);

  const fetchDeals = async () => {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('id, deal_title, seller_name, seller_email, seller_phone, buyer_name, current_balance, payment_amount, payments_remaining, property_address')
        .in('status', ['draft', 'approved'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDeals(data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast({
        title: "Error loading deals",
        description: "Could not load available deals",
        variant: "destructive"
      });
    }
  };

  const populateFromDeal = async (deal: Deal) => {
    console.log('Populating form from deal:', deal);
    
    // Find or create customer from deal seller information
    let customer = customers.find(c => 
      c.name.toLowerCase() === deal.seller_name.toLowerCase() ||
      (deal.seller_email && c.email === deal.seller_email)
    );

    // If no customer found, we need to create one
    if (!customer && deal.seller_name) {
      try {
        const { data: newCustomer, error } = await supabase
          .from('customers')
          .insert({
            name: deal.seller_name,
            email: deal.seller_email || null,
            phone: deal.seller_phone || null,
            status: 'active',
            notes: `Auto-created from deal: ${deal.deal_title}`
          })
          .select()
          .single();

        if (error) throw error;
        customer = newCustomer;
        
        toast({
          title: "Customer Created",
          description: `Created new customer record for ${deal.seller_name}`,
        });
      } catch (error) {
        console.error('Error creating customer:', error);
        toast({
          title: "Warning",
          description: "Could not create customer record. Please select manually.",
          variant: "destructive"
        });
      }
    }

    if (customer) {
      console.log('Setting customer ID:', customer.id);
      updateFormData('customerId', customer.id);
    }

    updateFormData('title', `Land Contract Purchase - ${deal.deal_title}`);
    updateFormData('description', `Purchase offer for land contract: ${deal.deal_title}${deal.property_address ? ` - ${deal.property_address}` : ''}`);

    // Update line items with deal information
    if (deal.current_balance) {
      updateLineItem(0, 'unit_price', deal.current_balance);
      updateLineItem(0, 'line_total', deal.current_balance);
    }

    // Set payment information
    if (deal.payments_remaining) {
      setNumberOfPayments(deal.payments_remaining.toString());
    }

    setAdditionalDetails(`Deal Information:
• Property: ${deal.property_address || 'N/A'}
• Buyer: ${deal.buyer_name}
• Current Balance: $${deal.current_balance?.toLocaleString() || 'N/A'}
• Monthly Payment: $${deal.payment_amount?.toLocaleString() || 'N/A'}
• Payments Remaining: ${deal.payments_remaining || 'N/A'}`);
    
    console.log('Form populated with deal data');
  };

  const handleDealSelection = async (dealId: string) => {
    setSelectedDealId(dealId);
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      populateFromDeal(deal);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', {
      formData,
      lineItems,
      firstPaymentDate,
      numberOfPayments
    });

    if (!validateForm()) {
      console.log('Form validation failed:', formData);
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields: Customer and Title",
        variant: "destructive"
      });
      return;
    }

    console.log('Form validation passed, submitting quote...');

    const quoteNotes = `${notes}

PAYMENT DETAILS:
First Payment Date: ${firstPaymentDate}
Number of Payments: ${numberOfPayments}

${additionalDetails ? `ADDITIONAL DETAILS:\n${additionalDetails}` : ''}`.trim();

    await submitQuote({ notes: quoteNotes });
  };

  const { subtotal, total } = calculateTotals();

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Customer Purchase Quote</CardTitle>
        <p className="text-muted-foreground">Land Contract Purchase Offer</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Deal Selection Section */}
          {!selectedDeal && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Deal (Optional)</h3>
              <div className="space-y-2">
                <Label htmlFor="dealSelect">Choose from Approved Deals</Label>
                <Select value={selectedDealId} onValueChange={handleDealSelection}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a deal to populate quote information..." />
                  </SelectTrigger>
                  <SelectContent>
                    {deals.map((deal) => (
                      <SelectItem key={deal.id} value={deal.id}>
                        {deal.deal_title} - {deal.seller_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {selectedDeal && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Selected Deal: {selectedDeal.deal_title}</h4>
              <p className="text-sm text-blue-700">
                Seller: {selectedDeal.seller_name} | Buyer: {selectedDeal.buyer_name}
              </p>
            </div>
          )}

          <Separator />

          <BasicQuoteFields
            customers={customers}
            formData={formData}
            onUpdateFormData={updateFormData}
          />

          <Separator />

          {/* Financial Details Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Financial Details</h3>
            <LineItemsSection
              lineItems={lineItems}
              onUpdateLineItem={updateLineItem}
              onRemoveLineItem={removeLineItem}
              onAddLineItem={addLineItem}
            />
          </div>

          <Separator />

          {/* Payment Information Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Payment Purchase Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstPaymentDate">First Payment Date We're Purchasing</Label>
                <Input
                  id="firstPaymentDate"
                  type="date"
                  value={firstPaymentDate}
                  onChange={(e) => setFirstPaymentDate(e.target.value)}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numberOfPayments">Number of Payments We're Purchasing</Label>
                <Input
                  id="numberOfPayments"
                  type="number"
                  value={numberOfPayments}
                  onChange={(e) => setNumberOfPayments(e.target.value)}
                  placeholder="Enter number of payments"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Additional Details Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="additionalDetails">Special Terms & Conditions</Label>
                <Textarea
                  id="additionalDetails"
                  value={additionalDetails}
                  onChange={(e) => setAdditionalDetails(e.target.value)}
                  placeholder="Enter any special terms, conditions, or additional details for this quote..."
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Internal Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes (not shown to customer)"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Quote Totals */}
          <div className="bg-muted/30 p-6 rounded-lg">
            <div className="text-right space-y-3">
              <div className="flex justify-between text-lg">
                <span className="font-medium">Gross Purchase Price:</span>
                <span className="font-medium">${Math.abs(subtotal).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Less: Customer Deductions:</span>
                <span>${(Math.abs(subtotal) - total).toFixed(2)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-xl text-primary">
                <span>Net Amount to Customer:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Professional Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-2">Important Terms & Conditions</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• This quote is subject to due diligence confirmation of all contract terms and conditions</li>
              <li>• Final pricing depends on successful completion of title work and property appraisal</li>
              <li>• The actual purchase price may vary based on our findings during the due diligence process</li>
              <li>• Quote is valid for 30 days from the date issued</li>
              <li>• All closing costs and fees are estimated and subject to change</li>
            </ul>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (editingQuote ? "Updating Quote..." : "Creating Quote...") : (editingQuote ? "Update Quote" : "Generate Quote")}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CustomerQuoteForm;